import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Room, RoomEvent, TokenSource, type TokenSourceFetchOptions } from 'livekit-client';

import { toastAlert } from '@/components/livekit/livekit/alert-toast';

import { dispatchAgentWeb } from '@/services/call.service';

import type { DispatchAgentRequest } from '@/types/call.type';
import type { LivekitConfig } from '@/types/livekit.type';

export function useRoom(appConfig: LivekitConfig) {
  const aborted = useRef(false);
  const room = useMemo(() => new Room(), []);
  const [isSessionActive, setIsSessionActive] = useState(false);

  useEffect(() => {
    function onDisconnected() {
      setIsSessionActive(false);
    }

    function onMediaDevicesError(error: Error) {
      toastAlert({
        title: 'Encountered an error with your media devices',
        description: `${error.name}: ${error.message}`,
      });
    }

    room.on(RoomEvent.Disconnected, onDisconnected);
    room.on(RoomEvent.MediaDevicesError, onMediaDevicesError);

    return () => {
      room.off(RoomEvent.Disconnected, onDisconnected);
      room.off(RoomEvent.MediaDevicesError, onMediaDevicesError);
    };
  }, [room]);

  useEffect(() => {
    return () => {
      aborted.current = true;
      room.disconnect();
    };
  }, [room]);

  const tokenSource = useMemo(
    () =>
      TokenSource.custom(async (options: TokenSourceFetchOptions) => {
        const payload: DispatchAgentRequest = {
          agentId: options.agentName,
          metadata: JSON.parse(options.agentMetadata),
        };
        const response = await dispatchAgentWeb(payload);
        return response;
      }),
    [appConfig]
  );

  const startSession = useCallback(
    (agentId: string, metadata: Record<string, string>) => {
      setIsSessionActive(true);

      if (room.state === 'disconnected') {
        const { isPreConnectBufferEnabled } = appConfig;
        Promise.all([
          room.localParticipant.setMicrophoneEnabled(true, undefined, {
            preConnectBuffer: isPreConnectBufferEnabled,
          }),
          tokenSource
            .fetch({ agentName: agentId, agentMetadata: JSON.stringify(metadata) })
            .then(connectionDetails => {
              room.connect(connectionDetails.serverUrl, connectionDetails.participantToken);
            })
            .catch(() => {
              //Ignore
            }),
        ]).catch(error => {
          if (aborted.current) {
            // Once the effect has cleaned up after itself, drop any errors
            //
            // These errors are likely caused by this effect rerunning rapidly,
            // resulting in a previous run `disconnect` running in parallel with
            // a current run `connect`
            return;
          }

          toastAlert({
            title: 'There was an error connecting to the agent',
            description: `${error.name}: ${error.message}`,
          });
        });
      }
    },
    [room, appConfig, tokenSource]
  );

  const endSession = useCallback(() => {
    setIsSessionActive(false);
  }, []);

  return { room, isSessionActive, startSession, endSession };
}
